import { useState } from 'react';
import { Code2, Key, Zap, Shield, DollarSign, Webhook, BookOpen } from 'lucide-react';
import { GlassCard, GradientText, PageHeader, Badge } from '../ui/index.js';

type Tab = 'python' | 'node' | 'curl' | 'langchain';

export function PlatformDeveloperGuidePage() {
  const [tab, setTab] = useState<Tab>('python');

  const baseUrl = 'https://router.rule42.ai/v1';

  const codeExamples: Record<Tab, string> = {
    python: `from openai import OpenAI

client = OpenAI(
    base_url="${baseUrl}",
    api_key="r42-...",  # Your API key from the API Keys page
)

# Simple chat completion
response = client.chat.completions.create(
    model="rule42/anthropic/claude-sonnet-4.6",
    messages=[{"role": "user", "content": "Hello!"}],
)

print(response.choices[0].message.content)
print(f"Cost: \${response.usage.cost}")

# Streaming
stream = client.chat.completions.create(
    model="rule42/anthropic/claude-sonnet-4.6",
    messages=[{"role": "user", "content": "Write a poem"}],
    stream=True,
)
for chunk in stream:
    if chunk.choices[0].delta.content:
        print(chunk.choices[0].delta.content, end="")

# BYOK (Bring Your Own Key) — use your own provider API key
response = client.chat.completions.create(
    model="rule42/anthropic/claude-sonnet-4.6",
    messages=[{"role": "user", "content": "Hello!"}],
    extra_headers={"x-byok-key": "sk-ant-..."},  # Your Anthropic key
)
# With BYOK: no token charges, only invocation billing`,
    node: `import OpenAI from "openai";

const client = new OpenAI({
  baseURL: "${baseUrl}",
  apiKey: "r42-...",  // Your API key from the API Keys page
});

// Simple chat completion
const response = await client.chat.completions.create({
  model: "rule42/anthropic/claude-sonnet-4.6",
  messages: [{ role: "user", content: "Hello!" }],
});

console.log(response.choices[0].message.content);
console.log(\`Cost: $\{response.usage.cost}\`);

// Streaming
const stream = await client.chat.completions.create({
  model: "rule42/anthropic/claude-sonnet-4.6",
  messages: [{ role: "user", content: "Write a poem" }],
  stream: true,
});

for await (const chunk of stream) {
  const content = chunk.choices[0]?.delta?.content;
  if (content) process.stdout.write(content);
}

// BYOK (Bring Your Own Key)
const byokResponse = await client.chat.completions.create({
  model: "rule42/anthropic/claude-sonnet-4.6",
  messages: [{ role: "user", content: "Hello!" }],
}, { headers: { "x-byok-key": "sk-ant-..." } });
// With BYOK: no token charges, only invocation billing`,
    curl: `# Simple chat completion
curl ${baseUrl}/chat/completions \\
  -H "Authorization: Bearer r42-..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "rule42/anthropic/claude-sonnet-4.6",
    "messages": [{"role": "user", "content": "Hello!"}]
  }'

# Streaming
curl ${baseUrl}/chat/completions \\
  -H "Authorization: Bearer r42-..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "rule42/anthropic/claude-sonnet-4.6",
    "messages": [{"role": "user", "content": "Write a poem"}],
    "stream": true
  }'

# BYOK (Bring Your Own Key)
curl ${baseUrl}/chat/completions \\
  -H "Authorization: Bearer r42-..." \\
  -H "x-byok-key: sk-ant-..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "rule42/anthropic/claude-sonnet-4.6",
    "messages": [{"role": "user", "content": "Hello!"}]
  }'

# List available models
curl ${baseUrl}/models \\
  -H "Authorization: Bearer r42-..."

# Check usage
curl ${baseUrl}/usage \\
  -H "Authorization: Bearer r42-..."

# Check credit balance
curl ${baseUrl}/credits/balance \\
  -H "Authorization: Bearer r42-..."`,
    langchain: `from langchain_openai import ChatOpenAI
from langchain_core.messages import HumanMessage

llm = ChatOpenAI(
    base_url="${baseUrl}",
    api_key="r42-...",  # Your API key from the API Keys page
    model="rule42/anthropic/claude-sonnet-4.6",
)

# Simple invocation
response = llm.invoke([HumanMessage(content="Hello!")])
print(response.content)

# Streaming
for chunk in llm.stream([HumanMessage(content="Write a poem")]):
    print(chunk.content, end="", flush=True)

# BYOK (Bring Your Own Key)
llm_byok = ChatOpenAI(
    base_url="${baseUrl}",
    api_key="r42-...",
    model="rule42/anthropic/claude-sonnet-4.6",
    default_headers={"x-byok-key": "sk-ant-..."},
)
# With BYOK: no token charges, only invocation billing`,
  };

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={<GradientText>Developer Guide</GradientText>}
        subtitle="Everything you need to integrate with the Rule42 Router — an OpenAI-compatible AI gateway."
      />

      {/* Quick Start */}
      <GlassCard className="space-y-4">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-amber-300" />
          <h3 className="text-sm font-semibold text-white">Quick Start</h3>
        </div>
        <p className="text-sm text-white/60">
          The Rule42 Router is an OpenAI-compatible API gateway. Point any OpenAI SDK at it and you're done.
          Every request is authenticated, finance-checked, and cost-logged automatically.
        </p>
        <div className="rounded-lg bg-black/40 px-4 py-3">
          <code className="text-sm text-emerald-300 font-mono">Base URL: {baseUrl}</code>
        </div>
      </GlassCard>

      {/* Authentication */}
      <GlassCard className="space-y-3">
        <div className="flex items-center gap-2">
          <Key className="h-4 w-4 text-indigo-300" />
          <h3 className="text-sm font-semibold text-white">Authentication</h3>
        </div>
        <div className="space-y-2 text-sm text-white/60">
          <p>Two authentication methods are supported:</p>
          <div className="ml-4 space-y-2">
            <div>
              <Badge variant="success">JWT</Badge>
              <span className="ml-2">For first-party apps (ANDL, cockpit):</span>
              <code className="ml-2 text-amber-200">Authorization: Bearer &lt;JWT&gt;</code>
            </div>
            <div>
              <Badge variant="info">API Key</Badge>
              <span className="ml-2">For 3rd party products:</span>
              <code className="ml-2 text-amber-200">Authorization: Bearer r42-...</code>
              <span className="ml-2">or</span>
              <code className="ml-2 text-amber-200">x-api-key: r42-...</code>
            </div>
          </div>
          <p className="mt-2">Every API key is tied to a registered rule42 user and charges their org. Generate keys in the <strong className="text-white/80">API Keys</strong> page.</p>
        </div>
      </GlassCard>

      {/* BYOK */}
      <GlassCard className="space-y-3 border-sky-500/20 bg-sky-500/5">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-sky-300" />
          <h3 className="text-sm font-semibold text-white">BYOK — Bring Your Own Key</h3>
          <Badge variant="info">Optional</Badge>
        </div>
        <div className="space-y-2 text-sm text-white/60">
          <p>If you have your own API key for a provider (Anthropic, OpenAI, etc.), you can use it instead of the platform's key:</p>
          <div className="ml-4 space-y-1">
            <div><code className="text-amber-200">x-byok-key: sk-ant-...</code> — header</div>
            <div><code className="text-amber-200">"byok_key": "sk-ant-..."</code> — in request body</div>
          </div>
          <div className="mt-2 rounded-lg bg-black/30 p-3">
            <p className="text-xs text-white/50">
              <strong className="text-sky-300">With BYOK:</strong> No token usage charges. The router uses your key for the upstream call.
              A flat per-invocation billing event is still emitted for metering.
            </p>
            <p className="text-xs text-white/50 mt-1">
              <strong className="text-emerald-300">Without BYOK:</strong> Full token usage charges apply. The router uses the platform's key
              and charges based on the model's sale price per million tokens.
            </p>
          </div>
          <p className="text-xs text-white/40">Note: BYOK is only supported on models with the <code>allow_byok</code> flag enabled.</p>
        </div>
      </GlassCard>

      {/* Code Examples */}
      <GlassCard className="space-y-3">
        <div className="flex items-center gap-2">
          <Code2 className="h-4 w-4 text-emerald-300" />
          <h3 className="text-sm font-semibold text-white">Code Examples</h3>
        </div>
        <div className="flex gap-1 border-b border-white/10">
          {(['python', 'node', 'curl', 'langchain'] as Tab[]).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-xs font-medium transition ${tab === t ? 'border-b-2 border-indigo-400 text-white' : 'text-white/40 hover:text-white/70'}`}>
              {t === 'python' ? 'Python' : t === 'node' ? 'Node.js' : t === 'curl' ? 'cURL' : 'LangChain'}
            </button>
          ))}
        </div>
        <pre className="overflow-auto rounded-lg bg-black/40 p-4 text-xs text-emerald-200 font-mono leading-relaxed">
          {codeExamples[tab]}
        </pre>
      </GlassCard>

      {/* Available Models */}
      <GlassCard className="space-y-3">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-purple-300" />
          <h3 className="text-sm font-semibold text-white">Available Models</h3>
        </div>
        <div className="text-sm text-white/60 space-y-2">
          <p><code className="text-amber-200">GET {baseUrl}/models</code> — returns the catalog in OpenAI format.</p>
          <p>Model IDs accept multiple formats:</p>
          <div className="ml-4 space-y-1 text-xs">
            <div><code className="text-emerald-200">rule42/anthropic/claude-sonnet-4.6</code> — full ID</div>
            <div><code className="text-emerald-200">anthropic/claude-sonnet-4.6</code> — without prefix</div>
            <div><code className="text-emerald-200">claude-sonnet-4.6</code> — bare name</div>
          </div>
          <p className="mt-2">All three resolve to the same model.</p>
        </div>
      </GlassCard>

      {/* Cost & Billing */}
      <GlassCard className="space-y-3">
        <div className="flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-emerald-300" />
          <h3 className="text-sm font-semibold text-white">Cost & Billing</h3>
        </div>
        <div className="text-sm text-white/60 space-y-2">
          <p>Every response includes cost information in the <code className="text-amber-200">usage</code> field:</p>
          <div className="ml-4 space-y-1 text-xs">
            <div><code className="text-emerald-200">usage.cost</code> — sale price (what you're charged)</div>
            <div><code className="text-emerald-200">usage.upstream_cost</code> — raw upstream cost (for reference)</div>
          </div>
          <p className="mt-2">The router automatically logs cost events to the finance system. No manual billing calls needed.</p>
          <p className="mt-1">Check your usage: <code className="text-amber-200">GET {baseUrl}/usage</code></p>
          <p>Check your balance: <code className="text-amber-200">GET {baseUrl}/credits/balance</code></p>
        </div>
      </GlassCard>

      {/* Webhooks */}
      <GlassCard className="space-y-3">
        <div className="flex items-center gap-2">
          <Webhook className="h-4 w-4 text-cyan-300" />
          <h3 className="text-sm font-semibold text-white">Webhooks</h3>
        </div>
        <div className="text-sm text-white/60 space-y-2">
          <p>Register a webhook URL to receive signed callbacks:</p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="info">cost.recorded</Badge>
            <Badge variant="info">rate_limit.exceeded</Badge>
            <Badge variant="info">api_key.revoked</Badge>
            <Badge variant="info">credit.threshold_reached</Badge>
            <Badge variant="info">request.failed</Badge>
          </div>
          <p className="mt-2">Payloads are HMAC-SHA256 signed. Verify with the <code className="text-amber-200">X-Rule42-Signature</code> header.</p>
        </div>
      </GlassCard>

      {/* Error Handling */}
      <GlassCard className="space-y-3">
        <h3 className="text-sm font-semibold text-white">Error Handling</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-white/40">
              <th className="pb-2 pr-4">Status</th>
              <th className="pb-2 pr-4">Meaning</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-white/5"><td className="py-1 pr-4"><Badge variant="error">401</Badge></td><td className="py-1 text-white/60">Invalid API key or JWT</td></tr>
            <tr className="border-b border-white/5"><td className="py-1 pr-4"><Badge variant="error">402</Badge></td><td className="py-1 text-white/60">Insufficient credits</td></tr>
            <tr className="border-b border-white/5"><td className="py-1 pr-4"><Badge variant="error">403</Badge></td><td className="py-1 text-white/60">Not authorized / guardrail blocked</td></tr>
            <tr className="border-b border-white/5"><td className="py-1 pr-4"><Badge variant="error">429</Badge></td><td className="py-1 text-white/60">Rate limit exceeded. The message says which limit, whose, how much is used, when it resets and who can raise it; the same is in <code>reason</code>, <code>scope</code>, <code>limit</code>, <code>used</code>, <code>unit</code> and <code>resetsAt</code>, beside the X-RateLimit-* headers</td></tr>
            <tr className="border-b border-white/5"><td className="py-1 pr-4"><Badge variant="error">502</Badge></td><td className="py-1 text-white/60">Upstream provider error</td></tr>
            <tr><td className="py-1 pr-4"><Badge variant="error">504</Badge></td><td className="py-1 text-white/60">Upstream timeout</td></tr>
          </tbody>
        </table>
      </GlassCard>

      {/* API Docs */}
      <GlassCard className="space-y-2">
        <h3 className="text-sm font-semibold text-white">Interactive API Documentation</h3>
        <p className="text-sm text-white/60">Full OpenAPI/Swagger spec available at <code className="text-amber-200">https://router.rule42.ai/docs</code></p>
      </GlassCard>
    </div>
  );
}
